import { JobWithTasks } from '../../hooks/use-query-jobs-with-tasks';
import { JobWithoutTasks as JobWithoutTask } from '../../hooks/use-query-jobs-without-tasks';
import { Task } from '../../hooks/use-query-tasks';
import { DateUtils } from '../../utils/date-utils';
import {
  getDeliveryStatus,
  getTurnaroundDays,
} from '../../utils/turnaround-utils';
import { getTaskStatusLabel } from '../common/task-status-badge';

import { TaskStatusEnum } from './types/task-status-enum';

type Maybe<T> = T | null | undefined;

type Config<T> = {
  visibilityKey: string;
  label: string;
  getValue: (item: T) => Maybe<string | number | Date>;
}[];

const jobConfig: Config<JobWithTasks> = [
  {
    visibilityKey: 'debtor',
    label: 'Debtor',
    getValue: (job) => job.client?.name,
  },
  { visibilityKey: 'job', label: 'Job', getValue: (job) => job.name },
  {
    visibilityKey: 'partner',
    label: 'Partner',
    getValue: (job) => job.partner?.fullName,
  },
  {
    visibilityKey: 'manager',
    label: 'Manager',
    getValue: (job) => job.manager?.fullName,
  },
  {
    visibilityKey: 'staff',
    label: 'Staff',
    getValue: (job) => job.staff?.fullName,
  },
  {
    visibilityKey: 'assignee',
    label: 'Assignee',
    getValue: (job) => job.assignee?.fullName,
  },
  {
    visibilityKey: 'status',
    label: 'Status',
    getValue: (job) => job.status.name,
  },
  {
    visibilityKey: 'dueDate',
    label: 'Due date',
    getValue: (job) => job.dueDate,
  },
  {
    visibilityKey: 'dueDate',
    label: 'Completed date',
    getValue: (job) => job.completedDate,
  },
  {
    visibilityKey: 'dueDate',
    label: 'Closed date',
    getValue: (job) => job.closedDate,
  },
  { visibilityKey: 'budget', label: 'Budget', getValue: (job) => job.budget },
  {
    visibilityKey: 'tasks',
    label: 'Tasks',
    getValue: (job) => job.tasks?.length,
  },
  {
    visibilityKey: 'tasks',
    label: 'Not started tasks',
    getValue: (job) =>
      job.tasks?.filter(
        (task) => task.status === TaskStatusEnum.valuesEnum.NotStarted
      ).length,
  },
  {
    visibilityKey: 'tasks',
    label: 'In progress tasks',
    getValue: (job) =>
      job.tasks?.filter(
        (task) => task.status === TaskStatusEnum.valuesEnum.InProgress
      ).length,
  },
  {
    visibilityKey: 'tasks',
    label: 'On hold tasks',
    getValue: (job) =>
      job.tasks?.filter(
        (task) => task.status === TaskStatusEnum.valuesEnum.OnHold
      ).length,
  },
  {
    visibilityKey: 'tasks',
    label: 'Ready for review tasks',
    getValue: (job) =>
      job.tasks?.filter(
        (task) => task.status === TaskStatusEnum.valuesEnum.ReadyForReview
      ).length,
  },
  {
    visibilityKey: 'tasks',
    label: 'Done tasks',
    getValue: (job) =>
      job.tasks?.filter(
        (task) => task.status === TaskStatusEnum.valuesEnum.Done
      ).length,
  },
  {
    visibilityKey: 'tasks',
    label: 'Cancelled tasks',
    getValue: (job) =>
      job.tasks?.filter(
        (task) => task.status === TaskStatusEnum.valuesEnum.Cancelled
      ).length,
  },
];

const taskConfig: Config<Task> = [
  {
    visibilityKey: 'debtor',
    label: 'Debtor',
    getValue: (task) => task.job?.client?.name,
  },
  { visibilityKey: 'job', label: 'Job', getValue: (task) => task.job?.name },
  { visibilityKey: 'task', label: 'Task', getValue: (task) => task.name },
  {
    visibilityKey: 'client',
    label: 'Client',
    getValue: (task) => task.entity?.name,
  },
  {
    visibilityKey: 'assigned',
    label: 'Assigned',
    getValue: (task) => task.assignee?.fullName,
  },
  {
    visibilityKey: 'status',
    label: 'Status',
    getValue: (task) => task.status && getTaskStatusLabel(task.status),
  },
  {
    visibilityKey: 'dueDate',
    label: 'Due date',
    getValue: (task) => task.dueDate,
  },
  {
    visibilityKey: 'dueDate',
    label: 'Completed date',
    getValue: (task) => task.completedDate,
  },
  {
    visibilityKey: 'estimatedTime',
    label: 'Estimated hours',
    getValue: (task) => task.estimateHoursDisplay,
  },
];

const completedJobConfig: Config<JobWithoutTask> = [
  {
    visibilityKey: 'partner',
    label: 'Partner',
    getValue: (job) => job.partner?.fullName,
  },
  {
    visibilityKey: 'manager',
    label: 'Manager',
    getValue: (job) => job.manager?.fullName,
  },
  {
    visibilityKey: 'staff',
    label: 'Staff',
    getValue: (job) => job.staff?.fullName,
  },
  {
    visibilityKey: 'assignee',
    label: 'Assignee',
    getValue: (job) => job.assignee?.fullName,
  },
  {
    visibilityKey: 'job',
    label: 'Job',
    getValue: (job) => job.name,
  },
  {
    visibilityKey: 'debtor',
    label: 'Debtor',
    getValue: (job) => job.client?.name,
  },
  {
    visibilityKey: 'startDate',
    label: 'Start Date',
    getValue: (job) => job.startDate,
  },
  {
    visibilityKey: 'dueDate',
    label: 'Due Date',
    getValue: (job) => job.dueDate,
  },
  {
    visibilityKey: 'completedDate',
    label: 'Completed Date',
    getValue: (job) => job.completedDate,
  },
  {
    visibilityKey: 'turnaround',
    label: 'Turnaround',
    getValue: (job) => getTurnaroundDays(job.completedDate, job.startDate),
  },
  {
    visibilityKey: 'delivery',
    label: 'Delivery',
    getValue: (job) => getDeliveryStatus(job.completedDate, job.dueDate),
  },
];

const toCsv =
  <T>(_config: Config<T>) =>
  (visibleProperties: { [key: string]: boolean }) => {
    const config = _config.filter(
      ({ visibilityKey }) => visibleProperties[visibilityKey]
    );
    return (items: T[]) =>
      [
        config.map(({ label }) => label),
        ...items.map((item) => config.map(({ getValue }) => getValue(item))),
      ]
        .map((values) =>
          values
            .map((value) => {
              if (typeof value === 'string') {
                return JSON.stringify(value);
              }
              if (value instanceof Date) {
                return JSON.stringify(DateUtils.formatDateShort(value));
              }
              return value;
            })
            .join(',')
        )
        .join('\n');
  };

export const jobsToCsv = toCsv(jobConfig);
export const tasksToCsv = toCsv(taskConfig);
export const completedJobsToCsv = toCsv(completedJobConfig);
